import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchRevenueDetails, fetchTotalMessageDetails, fetchTotalOrderDetails } from "../services/API";
import { subDays } from "date-fns";

// Define state interface
interface AnalyticsState {
  status: "idle" | "loading" | "succeeded" | "failed";
  revenue: number;
  orderCount: number;
  totalMessages: {
    total: number;
    user: number;
    agent: number;
    media: number;
    text: number;
  };
  byStatus: Record<string, number>;
  error: string | null;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

const today = new Date();
const initialState: AnalyticsState = {
  status: "idle",
  revenue: 0,
  orderCount: 0,
  totalMessages: {
    total: 0,
    user: 0,
    agent: 0,
    media: 0,
    text: 0
  },
  byStatus: {},
  error: null,
  dateRange: {
    startDate: subDays(today, 30),
    endDate: today
  }
};

// Async thunk to fetch revenue details
export const fetchRevenueAnalytics = createAsyncThunk<
  { revenue: number; byStatus: Record<string, number> },
  { business: string; startDate: Date; endDate: Date },
  { rejectValue: string }
>("analytics/fetchRevenueAnalytics", async ({ business, startDate, endDate }, { rejectWithValue }) => {
  try {
    const data = await fetchRevenueDetails(business,startDate,endDate);
    return { revenue: data.revenue.total, byStatus: data.revenue.byStatus };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch revenue details");
  }
});

//Async thunk to fetch order details
export const fetchOrderAnalytics = createAsyncThunk<
  { order: number; byStatus: Record<string, number> },
  { business: string; startDate: Date; endDate: Date },
  { rejectValue: string }
>("analytics/fetchTotalOrderAnalytics", async ({ business, startDate, endDate }, { rejectWithValue }) => {
  try {
    const data = await fetchTotalOrderDetails(business,startDate,endDate);
    return { order: data.count.total, byStatus: data.count.byStatus };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch revenue details");
  }
});

// Async thunk to fetch total message details
export const fetchMessageAnalytics = createAsyncThunk<
  { 
    total: number; 
    user: number; 
    agent: number; 
    media: number; 
    text: number 
  },
  { botId: string; startDate: Date; endDate: Date },
  { rejectValue: string }
>("analytics/fetchTotalMessageAnalytics", async ({ botId, startDate, endDate }, { rejectWithValue }) => {
  try {
    const data = await fetchTotalMessageDetails(botId, startDate, endDate);
    return {
      total: data.total_messages,
      user: data.user_messages,
      agent: data.agent_messages,
      media: data.media_messages,
      text: data.text_messages
    };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch message details");
  }
});

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    resetAnalytics(state) {
      state.status = "idle";
      state.revenue = 0;
      state.orderCount = 0;
      state.totalMessages = {
        total: 0,
        user: 0,
        agent: 0,
        media: 0,
        text: 0
      };
      state.byStatus = {};
      state.error = null;
    },
    setDateRange(state, action: PayloadAction<{ startDate: Date; endDate: Date }>) {
      state.dateRange = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRevenueAnalytics.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchRevenueAnalytics.fulfilled,
        (state, action: PayloadAction<{ revenue: number; byStatus: Record<string, number> }>) => {
          state.status = "succeeded";
          state.revenue = action.payload.revenue;
          state.byStatus = action.payload.byStatus;
          console.log("Fetched revenue analytics successfully:", action.payload);
        }
      )
      .addCase(fetchRevenueAnalytics.rejected, (state, action: PayloadAction<any>) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch revenue analytics";
        console.error("Failed to fetch revenue analytics:", action.payload);
      })
      .addCase(fetchOrderAnalytics.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchOrderAnalytics.fulfilled,
        (state, action: PayloadAction<{ order: number; byStatus: Record<string, number> }>) => {
          state.status = "succeeded";
          state.orderCount = action.payload.order;
          state.byStatus = action.payload.byStatus;
          console.log("Fetched total order analytics successfully:", action.payload);
        }
      )
      .addCase(fetchOrderAnalytics.rejected, (state, action: PayloadAction<any>) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch revenue analytics";
        console.error("Failed to fetch revenue analytics:", action.payload);
      })
      // Message Analytics Cases
      .addCase(fetchMessageAnalytics.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchMessageAnalytics.fulfilled,
        (state, action: PayloadAction<{ 
          total: number; 
          user: number; 
          agent: number; 
          media: number; 
          text: number 
        }>) => {
          state.status = "succeeded";
          state.totalMessages = action.payload;
          console.log("Fetched total message analytics successfully:", action.payload);
        }
      )
      .addCase(fetchMessageAnalytics.rejected, (state, action: PayloadAction<any>) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch message details";
        console.error("Failed to fetch message details:", action.payload);
      });
  },
});

export const { resetAnalytics,setDateRange } = analyticsSlice.actions;
export default analyticsSlice.reducer;
